import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Stack, useTheme, InputBase } from '@mui/material';
import { useUserStateContext } from '@/shared/components/UserStateProvider';
import { useGameTimeFormatter } from '@/features/games/hooks/useGameTimeFormatter';
import { displayWallClock } from '@/shared/services';
import { fromZonedTime, formatInTimeZone } from 'date-fns-tz';

export interface BoundaryExceededInfo {
  type: 'min' | 'max';
  enteredTime: Date;
  boundaryTime: Date;
  clampedTime: Date;
}

interface TimeAdjusterProps {
  title: string;
  time: string | Date; // Can be a formatted string or Date object
  onTimeChange?: (seconds: number) => void;
  stepSize?: number; // Default 5 seconds
  readOnly?: boolean; // If true, hides adjustment buttons
  minTime?: Date; // Minimum allowed time
  maxTime?: Date; // Maximum allowed time
  /** Called when user enters a time outside the allowed boundaries */
  onBoundaryExceeded?: (info: BoundaryExceededInfo) => void;
  sx?: object;
}

/**
 * Parse a time string (e.g., "10:41:08 PM") and apply it to a reference date in the specified timezone
 */
const parseTimeString = (timeStr: string, referenceDate: Date, timezone: string): Date | null => {
  // Try parsing formats like "10:41:08 PM", "10:41:08", "10:41 PM", "10:41"
  const timeRegex = /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i;
  const match = timeStr.trim().match(timeRegex);

  if (!match) return null;

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const seconds = match[3] ? parseInt(match[3], 10) : 0;
  const meridiem = match[4]?.toUpperCase();

  // Validate ranges
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59 || seconds < 0 || seconds > 59) {
    return null;
  }

  // Handle 12-hour format
  if (meridiem) {
    if (hours > 12) return null;
    if (meridiem === 'PM' && hours !== 12) {
      hours += 12;
    } else if (meridiem === 'AM' && hours === 12) {
      hours = 0;
    }
  }

  // Get the reference date's date portion in the target timezone
  const referenceDateStr = formatInTimeZone(referenceDate, timezone, 'yyyy-MM-dd');

  // Construct a date string with the new time in the target timezone
  const timeString = `${referenceDateStr}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

  // Convert from the target timezone to UTC
  return fromZonedTime(timeString, timezone);
};

export const TimeAdjuster: React.FC<TimeAdjusterProps> = ({
  title,
  time,
  onTimeChange,
  stepSize = 5,
  readOnly = false,
  minTime,
  maxTime,
  onBoundaryExceeded,
  sx
}) => {
  const {
    userProfile: { tz },
  } = useUserStateContext();
  const { getActiveTimezone } = useGameTimeFormatter();

  const theme = useTheme();

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { timeZone: browserTZ } = Intl.DateTimeFormat().resolvedOptions();

  // Get current time as Date object
  const currentTime = React.useMemo(() => {
    if (time instanceof Date) {
      return time;
    } else if (typeof time === 'string') {
      const dateObj = new Date(time);
      if (!isNaN(dateObj.getTime())) {
        return dateObj;
      }
    }
    return null;
  }, [time]);

  // Check if decrement/increment buttons should be disabled
  const canDecrement = React.useMemo(() => {
    if (!currentTime || !minTime) return true;
    return currentTime.getTime() - stepSize * 1000 >= minTime.getTime();
  }, [currentTime, minTime, stepSize]);

  const canIncrement = React.useMemo(() => {
    if (!currentTime || !maxTime) return true;
    return currentTime.getTime() + stepSize * 1000 <= maxTime.getTime();
  }, [currentTime, maxTime, stepSize]);

  // Format time display with timezone awareness
  const displayTime = React.useMemo(() => {
    let dateObj: Date;

    if (time instanceof Date) {
      dateObj = time;
    } else if (typeof time === 'string') {
      // Try to parse the string as a Date
      dateObj = new Date(time);

      // If parsing fails, return the original string
      if (isNaN(dateObj.getTime())) {
        return time;
      }
    } else {
      return String(time);
    }

    return displayWallClock(dateObj, { timezone: getActiveTimezone() });
  }, [time, getActiveTimezone]);

  const handleDecrement = () => {
    if (canDecrement) {
      onTimeChange?.(-stepSize);
    }
  };

  const handleIncrement = () => {
    if (canIncrement) {
      onTimeChange?.(stepSize);
    }
  };

  // Start editing mode
  const handleStartEditing = () => {
    if (readOnly || !onTimeChange) return;
    setIsEditing(true);
    setEditValue(displayTime);
  };

  // Handle time input submission
  const handleSubmitEdit = () => {
    if (!currentTime) {
      setIsEditing(false);
      return;
    }

    const newTime = parseTimeString(editValue, currentTime, getActiveTimezone());

    if (newTime) {
      // Calculate delta in seconds
      const deltaMs = newTime.getTime() - currentTime.getTime();
      const deltaSeconds = deltaMs / 1000;

      // Check constraints
      if (minTime && newTime.getTime() < minTime.getTime()) {
        // Clamp to min and notify
        const clampedDelta = (minTime.getTime() - currentTime.getTime()) / 1000;
        onTimeChange?.(clampedDelta);
        onBoundaryExceeded?.({
          type: 'min',
          enteredTime: newTime,
          boundaryTime: minTime,
          clampedTime: minTime,
        });
      } else if (maxTime && newTime.getTime() > maxTime.getTime()) {
        // Clamp to max and notify
        const clampedDelta = (maxTime.getTime() - currentTime.getTime()) / 1000;
        onTimeChange?.(clampedDelta);
        onBoundaryExceeded?.({
          type: 'max',
          enteredTime: newTime,
          boundaryTime: maxTime,
          clampedTime: maxTime,
        });
      } else if (deltaSeconds !== 0) {
        onTimeChange?.(deltaSeconds);
      }
    }

    setIsEditing(false);
  };

  // Handle keyboard events in edit mode
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmitEdit();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
    }
  };

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  return (
    <Box
      sx={{
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        minWidth: '125px',
        ...sx
      }}
    >
      {/* Title */}
      <Typography
        sx={{
          color: readOnly ? theme.palette.cx.text.tertiary : theme.palette.cx.text.secondary,
          fontSize: '14px',
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        {title}
      </Typography>

      {/* Time and Controls */}
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        {/* Time Display - Editable when not readOnly */}
        {isEditing ? (
          <InputBase
            inputRef={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSubmitEdit}
            onKeyDown={handleKeyDown}
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              color: theme.palette.cx.text.secondary,
              padding: 0,
              '& input': {
                padding: 0,
                width: '90px',
              },
            }}
          />
        ) : (
          <Typography
            onClick={handleStartEditing}
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              color: readOnly ? theme.palette.cx.text.tertiary : theme.palette.cx.text.secondary,
              cursor: readOnly ? 'default' : 'text',
              '&:hover': !readOnly ? {
                textDecoration: 'underline',
                textDecorationStyle: 'dotted',
              } : {},
            }}
          >
            {displayTime}
          </Typography>
        )}

        {/* Adjustment Buttons - Only show if not readOnly */}
        {!readOnly && (
          <Stack direction="row" spacing={1} sx={{ ml: 1 }}>
            {/* Decrement Button */}
            <Box
              onClick={handleDecrement}
              sx={{
                color: 'white',
                width: 16,
                height: 16,
                opacity: canDecrement ? 1 : 0.3,
                '&:hover': {
                  cursor: canDecrement ? 'pointer' : 'not-allowed',
                },
                position: 'relative',
                transition: 'opacity 0.2s',
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M1.57164 1.42847C1.45797 1.42847 1.34896 1.47362 1.26859 1.55399C1.18822 1.63437 1.14307 1.74337 1.14307 1.85704V5.57132C1.14307 5.8079 1.33507 5.9999 1.57164 5.9999H5.28592C5.39959 5.9999 5.5086 5.95474 5.58897 5.87437C5.66934 5.794 5.7145 5.68499 5.7145 5.57132C5.7145 5.45766 5.66934 5.34865 5.58897 5.26828C5.5086 5.18791 5.39959 5.14275 5.28592 5.14275H2.62307C3.58307 3.69647 5.51792 2.57132 7.85735 2.57132C10.2671 2.57132 12.1665 3.71304 13.4956 5.80161C13.5259 5.84911 13.5652 5.89019 13.6113 5.9225C13.6574 5.95481 13.7094 5.97773 13.7644 5.98993C13.8194 6.00214 13.8762 6.00339 13.9317 5.99363C13.9871 5.98387 14.0401 5.96328 14.0876 5.93304C14.1351 5.9028 14.1762 5.8635 14.2085 5.81738C14.2408 5.77126 14.2638 5.71923 14.276 5.66426C14.2882 5.60929 14.2894 5.55245 14.2797 5.49699C14.2699 5.44153 14.2493 5.38854 14.2191 5.34104C12.7488 3.03247 10.5905 1.71418 7.85735 1.71418C5.32364 1.71418 3.15678 2.89875 2.00021 4.53532V1.85704C2.00021 1.74337 1.95506 1.63437 1.87468 1.55399C1.79431 1.47362 1.6853 1.42847 1.57164 1.42847Z"
                  fill="white"
                />
                <path
                  d="M7.52387 11.4264L6.42593 11.2749L6.72881 7.67822H10.5527V8.73831H7.65638L7.50494 10.4041C7.77942 10.1486 8.20535 9.94036 8.74486 9.94036C10.0984 9.94036 10.9597 10.9342 10.9597 12.2309C10.9597 13.5655 9.91852 14.5782 8.5177 14.5782C7.18313 14.5782 6.2177 13.8494 6 12.5338L7.1642 12.3255C7.27778 13.0922 7.78889 13.5749 8.5177 13.5749C9.25597 13.5749 9.82387 13.0165 9.82387 12.2688C9.82387 11.521 9.34115 10.9247 8.58395 10.9247C8.09177 10.9247 7.72263 11.1519 7.52387 11.4264Z"
                  fill="white"
                />
              </svg>
            </Box>

            {/* Increment Button */}
            <Box
              onClick={handleIncrement}
              sx={{
                color: 'white',
                width: 16,
                height: 16,
                opacity: canIncrement ? 1 : 0.3,
                '&:hover': {
                  cursor: canIncrement ? 'pointer' : 'not-allowed',
                },
                position: 'relative',
                transition: 'opacity 0.2s',
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M14.1668 1.66675C14.2994 1.66675 14.4266 1.71943 14.5204 1.81319C14.6142 1.90696 14.6668 2.03414 14.6668 2.16675V5.83341C14.6668 5.96602 14.6142 6.0932 14.5204 6.18697C14.4266 6.28074 14.2994 6.33341 14.1668 6.33341H10.5002C10.3676 6.33341 10.2404 6.28074 10.1466 6.18697C10.0528 6.0932 10.0002 5.96602 10.0002 5.83341C10.0002 5.70081 10.0528 5.57363 10.1466 5.47986C10.2404 5.38609 10.3676 5.33341 10.5002 5.33341H13.1048C12.4002 4.40141 11.4868 3.64341 10.3522 3.29208C8.98592 2.86544 7.51674 2.90413 6.17484 3.40208C4.8215 3.90541 3.56817 4.92541 2.93884 6.07408C2.90805 6.13305 2.86581 6.18528 2.81459 6.22771C2.76336 6.27015 2.70419 6.30194 2.64052 6.32122C2.57686 6.3405 2.50999 6.34688 2.44383 6.33999C2.37767 6.3331 2.31355 6.31308 2.25522 6.2811C2.1969 6.24913 2.14554 6.20583 2.10415 6.15375C2.06277 6.10167 2.03219 6.04186 2.01421 5.97782C1.99624 5.91378 1.99122 5.84679 1.99945 5.78078C2.00769 5.71478 2.02901 5.65108 2.06217 5.59341C2.8235 4.20475 4.28217 3.03941 5.82617 2.46475C7.37507 1.88976 9.07094 1.84475 10.6482 2.33675C11.9002 2.72475 12.8975 3.50808 13.6668 4.43275V2.16675C13.6668 2.03414 13.7195 1.90696 13.8133 1.81319C13.9071 1.71943 14.0342 1.66675 14.1668 1.66675Z"
                  fill="white"
                />
                <path
                  d="M6.5644 11.4264L5.46645 11.2749L5.76933 7.67822H9.5932V8.73831H6.69691L6.54547 10.4041C6.81995 10.1486 7.24588 9.94036 7.78538 9.94036C9.13888 9.94036 10.0002 10.9342 10.0002 12.2309C10.0002 13.5655 8.95905 14.5782 7.55822 14.5782C6.22366 14.5782 5.25822 13.8494 5.04053 12.5338L6.20472 12.3255C6.31831 13.0922 6.82942 13.5749 7.55822 13.5749C8.29649 13.5749 8.8644 13.0165 8.8644 12.2688C8.8644 11.521 8.38168 10.9247 7.62448 10.9247C7.1323 10.9247 6.76316 11.1519 6.5644 11.4264Z"
                  fill="white"
                />
              </svg>
            </Box>
          </Stack>
        )}
      </Stack>

      {/* Bottom Border */}
      <Box
        sx={{
          width: '100%',
          height: '1px',
          backgroundColor: '#676774',
        }}
      />
    </Box>
  );
};
